import { Input, Directive } from '@angular/core';

import { ListResource } from '@resources/list.resource';
import { KeyedResource } from '@resources/keyed.resource';
import { LMLoggerService } from '@services/lm-logger.service';
import { AppConstants } from '@utils/app.constants';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMFormatUtilService } from '@services/utils';
import { Subscription } from 'rxjs';

@Directive()
export abstract class LMListContentBaseComponent {

  @Input() listUrlInfo: LMListUrlInfo;

  /**
   * The listSpec used to build
   * @type {LMListSpec}
   */
  @Input() set listSpec(listSpec: LMListSpec) {
    if (!listSpec) {
      return; // safety
    }

    this.lmLoggerService.log([ this, this.instanceId, 'set listSpec()' ]);
    this._listSpec = listSpec;

    /*
     * set the list resource for use in paging.
     */
    this.listResource = new ListResource({ resourceId: listSpec.resourceId });

    this.inDashboardPanel = listSpec.listType === 'dashboard';

    if (this.listUrlInfo?.sourceResource) {
      /**
       * if there is a "source" resource set the "from" resource for use in paging.
       * todo: doesn't seem like this is needed as sourceResource could be used instead.
       */
      this.fromResource = new KeyedResource({
        resourceId: this.listUrlInfo.sourceResource.resourceType,
        resourceType: this.listUrlInfo.sourceResource.resourceId
      });
    }
  };

  /**
   * Causes the datagrid to reload with the given set of filterCriteria
   */
  @Input() set filterCriteria(filterCriteria: LMFilterFieldCriteria[]) {
    this.lmLoggerService.trace([ this, this.instanceId, 'filterCriteria', filterCriteria ]);

    // check to make sure filterCriteria has changed from undefined to a null or an Array of at least 1 object
    if (this.m_filterCriteria === undefined && (filterCriteria === undefined || filterCriteria === null || filterCriteria.length === 0)) {
      // Then this is first time in and hence it just an initialization. Make sure to set
      // m_filterCriteria to null so 2nd time in with a null will run the filterPage.
      // This is done so that search form being executed w/o filter criteria will show
      // all records.
      this.m_filterCriteria = null;
      return;
    }

    this.m_filterCriteria = filterCriteria;

    // need to wait on the fromDataView to come in before calling filterCriteriaChanged
    if (this.listSpec.dependentList && !this.fromDataView) {
      return;
    }

    this.filterCriteriaChanged();
  }

  get filterCriteria(): LMFilterFieldCriteria[] {
    return this.m_filterCriteria;
  }

  @Input() set columnfilterOperators(filterOperators: Map<string, string>) {
    this.m_columnfilterOperators = filterOperators;
  }

  get columnfilterOperators(): Map<string, string> {
    return this.m_columnfilterOperators;
  }

  /**
   * Causes the datagrid to reload with the given set of filterCriteria
   */
  @Input() set filterListFilterCriteria(filterCriteria: LMFilterFieldCriteria[]) {
    this.lmLoggerService.trace([ this, this.instanceId, 'filterCriteria', filterCriteria ]);
    this.m_filterListFilterCriteria = filterCriteria;

    if (this.m_filterListFilterCriteria === undefined || this.m_filterListFilterCriteria === null) {
      return;
    }

    this.filterListCriteriaChanged();
  }

  get filterListFilterCriteria(): LMFilterFieldCriteria[] {
    return this.m_filterListFilterCriteria;
  }

  /**
   * Used to retrieve data when this list is a 'dependentList'
   */
  @Input() set fromDataView(fromDataView: LMDataView) {
    if (this.listSpec?.dependentList || this.listSpec?.busListSpec.showChangedValuesOnRight) {
      this.lmLoggerService.trace([ this, 'fromDataView' ]);
      if ((<LMListDataView>fromDataView)?.isTreeMoreLink) {
        fromDataView = null;
      }

      // if undefined then this is angular initializing so don't run fromDataViewChanged().
      if (fromDataView === undefined) {
        return;
      }

      const previousFromResource = this._fromDataView;
      this._fromDataView = fromDataView;

      // fromResource keys change we are on a new record so fetch new data
      // expected that  refresh is called if the fromDataView was only updated!
      if (!this.isSameFromResource(fromDataView, previousFromResource)) {
        this.allowDataLoading = false; //CHERYL what is this for!
        this.fromDataViewChanged();
      }
    }
  }

  public unSelectRow(rowToUnselect: number): void {
    // todo - implement me
  }

  private isSameFromResource(fromDataView1: LMDataView, fromDataView2: LMDataView): boolean {
    if (fromDataView1?.cubeInfo && fromDataView2?.cubeInfo) {
      // Resource Ids are just the cube mediator and they are the same for all dataviews.
      const dimTree1 = fromDataView1.cubeInfo.dimensions.map(dim => dim.dimVal).join(' ');
      const dimTree2 = fromDataView2.cubeInfo.dimensions.map(dim => dim.dimVal).join(' ');
      let retVal = dimTree1 === dimTree2;

      if (fromDataView1.cubeInfo.extraInfo && fromDataView2.cubeInfo.extraInfo) {
        if (fromDataView1.cubeInfo.extraInfo.periodKey) {
          retVal = retVal && (fromDataView1.cubeInfo.extraInfo.periodKey === fromDataView2.cubeInfo.extraInfo.periodKey);
        }
        if (fromDataView1.cubeInfo.extraInfo.periodView) {
          retVal = retVal && (fromDataView1.cubeInfo.extraInfo.periodView === fromDataView2.cubeInfo.extraInfo.periodView)
            && (fromDataView1.cubeInfo.extraInfo.pvPeriodIdx === fromDataView2.cubeInfo.extraInfo.pvPeriodIdx);
        }
      }
      return  retVal;
      // Use dataViewResId which is based on the view key of dataview.
      // return fromDataView1.dataViewResId === fromDataView2.dataViewResId;
    } else {
      const resourceId = fromDataView1 ? fromDataView1.resourceId : '';
      const newResourceId = fromDataView2 ? fromDataView2.resourceId : '';

      return resourceId === newResourceId;
    }
  }
  /**
   * The information needed to fill out data for a select list.
   */
  @Input() selectRequestData: SelectRequestData;

  /**
   * ListState for a list and all it's contents
   */
  @Input() listState: LMListState = {};

  /**
   * ListPersonalization for a list and all it's contents
   */
  @Input() listPersonalization: LMListPersonalization = {};

  @Input() set disabled(disabled: boolean) {
    this._disabled = disabled;
    // disabled ? this.clearEmptyMessage() : this.setNoDataMessage(this.filterCriteria && this.filterCriteria.length > 0)
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @Input() implementsManualLoad: boolean;

  @Input() manualLoadInfo: LMManualLoadInfo;

  protected selectListEditModeChanged = false;

  private _inSelectListEditMode = false;
  @Input() public set inSelectListEditMode(inSelectListEditMode: boolean) {
    this._inSelectListEditMode = inSelectListEditMode;
    this.inSelectedListEditModeChanged();
  }

  public get inSelectListEditMode(): boolean {
    return this._inSelectListEditMode;
  }

  protected _multiSelectedValues: LMMultiSelectedValue[];
  @Input() public set multiSelectedValues(multiSelectedValues: LMMultiSelectedValue[]) {
    this._multiSelectedValues = multiSelectedValues;
    this.multiSelectedValuesChanged();
  }

  public get multiSelectedValues(): LMMultiSelectedValue[] {
    return this._multiSelectedValues;
  }

  /**
   * Internal member variable, used to hold the filter field state.
   * Get set by using the filterCriteria @Input.
   */
  public _listSpec: LMListSpec;

  /**
   * interntal member variable, used by the view to determine if
   * this list is in a dashboard panel.
   */
  public inDashboardPanel: boolean;

  /**
   * Used for lm-page-standalone for each list implementation that uses it.
   */
  public pageSizes: number[] = AppConstants.PAGE_SIZES;

  public dataViewSet: LMDataViewSet;
  public savedSearches: LMDataViewSet;

  /**
   * Internal member variable, used to hold the filter field state.
   * Get set by using the filterCriteria @Input.
   */
  protected m_filterCriteria: LMFilterFieldCriteria[];

  /**
   * Internal member variable, used to hold internal filter field state.
   * Gets set by using the filterListFilterCritera @Input.
   */
  protected m_filterListFilterCriteria: LMFilterFieldCriteria[];

  protected m_columnfilterOperators: Map<string, string>;

  protected allowDataLoading: boolean;

  protected pagingSubscription: Subscription;

  /**
   * Used by get/set fromDataView typescript property methods
   * Set to undefined to distinguish it from a null fromDataView.
   * - undefined fromDataView means dependent list is not yet ready to be used.
   * - null fromDataView is a valid input value meaning that this dependent list should be treated as disabled.
   */
  protected _fromDataView = undefined;

  /**
   * Whether or not the list implementation (grid, list-view, tree-list, etc...) should be disabled or not.
   */
  protected _disabled;

  protected listResource: ListResource;
  protected fromResource: KeyedResource;

  private _noListDataMessage: SohoEmptyMessageOptions = null;
  private get noListDataMessage(): SohoEmptyMessageOptions {
    if (!this._noListDataMessage) {
      this._noListDataMessage = {
        title: Soho.Locale.translate('NoDataAvailable'),
        icon: this.listSpec?.listType !== 'multilist' ? 'icon-empty-no-data' : '',
        color: 'azure'
      };
    };

    return this._noListDataMessage;
  };

  private _noSearchDataMessage: SohoEmptyMessageOptions = null;
  private get noSearchDataMessage(): SohoEmptyMessageOptions {
    if (!this._noSearchDataMessage) {
      this._noSearchDataMessage = {
        title: Soho.Locale.translate('NoSearchResultsFound'),
        icon: this.listSpec?.listType !== 'multilist' ? 'icon-empty-generic': '',
        color: 'azure'
      };
    };

    return this._noSearchDataMessage;
  };

  // Search Empty Message displayed to inform the user
  // to do search to display records on the list
  private get useSearchToFindDataMessage(): SohoEmptyMessageOptions {
    return {
      title: Soho.Locale.translate('UseSearchToFindData'),
      icon: 'icon-empty-search-data',
      color: 'azure'
    };
  }

  private _noChartableDataAvailable: SohoEmptyMessageOptions = null;
  private get noChartableDataAvailable(): SohoEmptyMessageOptions {
    if (!this._noChartableDataAvailable) {
      this._noChartableDataAvailable = {
        title: Soho.Locale.translate('NoChartableDataAvailable'),
        icon: 'icon-empty-generic',
        color: 'azure'
      };
    };

    return this._noChartableDataAvailable;
  }

  private manualLoadMessage(manualLoadInfo: LMManualLoadInfo): SohoEmptyMessageOptions {
    // default message options
    const messageOptions: SohoEmptyMessageOptions = {
      title: Soho.Locale.translate('ManuallyLoadData'),
      icon: this.listSpec?.listType !== 'multilist' ? 'icon-empty-no-data': '',
      color: 'azure',
      button: {
        id: 'loadDataBtn',
        text: Soho.Locale.translate('LoadData'),
        isPrimary: true,
        click: (event) => {
          event.stopPropagation();
          this.allowDataLoading = true;
          this.loadManualData();
        }
      }
    };

    if (manualLoadInfo) {
      const dataViewHeader = this.dataViewSet && this.dataViewSet.header ? this.dataViewSet.header : undefined;
      if (manualLoadInfo.message) {
        messageOptions.title = this.lmFormatUtilService.formatMessageRule(manualLoadInfo.message, dataViewHeader);
      }

      if (manualLoadInfo.description) {
        messageOptions.info = this.lmFormatUtilService.formatMessageRule(manualLoadInfo.description, dataViewHeader);
      }

      if (manualLoadInfo.buttonText) {
        messageOptions.button.text = this.lmFormatUtilService.formatMessageRule(manualLoadInfo.buttonText, dataViewHeader);
      }

      if (manualLoadInfo.icon) {
        // must be the full soho empty message icon name
        if (this.lmThemeService.isValidSohoIconEmptyMessage(manualLoadInfo.icon)) {
          messageOptions.icon = manualLoadInfo.icon;
        }
      }
    }

    return messageOptions;
  }

  private noDataEmptyMessage(noDataEmptyMessageInfo: LMNoDataEmptyMessageInfo): SohoEmptyMessageOptions {
    // default message options
    const messageOptions: SohoEmptyMessageOptions = {
      icon: this.listSpec?.listType !== 'multilist' ? 'icon-empty-no-data' : '',
      color: 'azure'
    };

    if (noDataEmptyMessageInfo) {
      const dataViewHeader: LMDataView = this.dataViewSet && this.dataViewSet.header ? this.dataViewSet.header : undefined;
      messageOptions.title = this.lmFormatUtilService.formatMessageRule(noDataEmptyMessageInfo.message, dataViewHeader);

      if (noDataEmptyMessageInfo.description) {
        messageOptions.info = this.lmFormatUtilService.formatMessageRule(noDataEmptyMessageInfo.description, dataViewHeader);
      }

      if (noDataEmptyMessageInfo.icon) {
        // must be the full soho empty message icon name
        if (this.lmThemeService.isValidSohoIconEmptyMessage(noDataEmptyMessageInfo.icon)) {
          messageOptions.icon = noDataEmptyMessageInfo.icon;
        }
      }

      if (noDataEmptyMessageInfo.button) {
        const button: SohoEmptyMessageButtonOptions = {
          id: 'noDataBtn',
          text: this.lmFormatUtilService.formatMessageRule(noDataEmptyMessageInfo.button.title, dataViewHeader),
          isPrimary: true,
          click: (event) => {
            event.stopPropagation();
            const columnDef: ListGridColumn = { displayField: noDataEmptyMessageInfo.button };
            const dataView: LMDataView = this.dataViewSet && this.dataViewSet.header ? this.dataViewSet.header : undefined;
            this.onFormButtonClicked(columnDef, dataView, null, null, null, event, true);
          }
        };
        messageOptions.button = button;
      }
    }

    return messageOptions;
  }

  private static errorLoadingMessage: SohoEmptyMessageOptions = {
    icon: 'icon-empty-error-loading',
    color: 'azure'
  };

  /** public so it can be used in a template of any subclasses. */
  public emptyMessage: SohoEmptyMessageOptions = null;

  protected instanceId: string;

  /**
   * If ngOnDestroy is called, set this flag to true. this will be used
   * internally by the component for any extraneous calls still attached
   * to this component.
   */
  private m_componentDestroyed = false;

  protected constructor(
    protected lmLoggerService: LMLoggerService,
    protected lmThemeService: LMThemeService,
    protected lmFormatUtilService: LMFormatUtilService
  ) {
    this.instanceId = this.lmLoggerService.instanceId;
  }

  protected abstract build(): void;

  protected abstract fromDataViewChanged(): void;

  protected abstract filterCriteriaChanged(): void;

  protected abstract filterListCriteriaChanged(): void;

  protected inSelectedListEditModeChanged(): void {
  }

  public multiSelectedValuesChanged(): void {
  }

  public disableSaveInUserListViewMenu(): boolean {
    return true;
  }

  public applyDefaultUserListView(defaultULV: LMUserListViewItem): void {
  }

  public clearUserListView(): void {
    this.listState.userListViewItem = undefined;
  }

  protected abstract loadManualData(): void;

  protected abstract onFormButtonClicked(columnDef: ListGridColumn, dataView: LMDataView, row: any, cell: any, navigationParams: any, event: any, fromNoDataMessageFormButton?: boolean): void;

  public isManualLoading(): boolean {
    return !!(this.implementsManualLoad && !this.allowDataLoading);
  }

  get listSpec(): LMListSpec {
    return this._listSpec;
  }

  get fromDataView(): LMDataView {
    return this._fromDataView;
  }

  public getDataViewSet(): LMDataViewSet {
    return this.dataViewSet;
  }

  public getDataView(index: number) {
    if (!this.dataViewSet || this.dataViewSet.data.length <= 0) {
      return null;
    }
    return this.dataViewSet.data[ index ];
  }

  protected setEmptyMessage() {
    if (this.disabled) {
      this.clearEmptyMessage();
      return;
    }

    if (this.isManualLoading()) {
      this.emptyMessage = this.manualLoadMessage(this.manualLoadInfo);
      return;
    }

    const searchForm = this.listSpec.busListSpec.searchForm;
    if (searchForm && !searchForm.useSearchAsFilter && !(this.filterCriteria && this.filterCriteria.length > 0)) {
      this.emptyMessage = this.useSearchToFindDataMessage;
      return;
    }

    this.setNoDataMessage(this.filterCriteria && this.filterCriteria.length > 0);

  }

  protected clearEmptyMessage() {
    this.emptyMessage = null;
  }

  protected handleError(message: string, title: string) {
    const error: LMError = { message, title };
    this.setErrorLoadingMessage(error);
  }

  protected setErrorLoadingMessage(error: LMError) {
    this.emptyMessage = Object.assign(
      LMListContentBaseComponent.errorLoadingMessage,
      {
        title: error.title,
        info: error.message
      });
  }

  protected setNoDataMessage(noSearchData: boolean) {
    this.emptyMessage = noSearchData
      ? this.noSearchDataMessage
      : (this.listSpec.busListSpec.noDataEmptyMessageInfo)
        ? this.noDataEmptyMessage(this.listSpec.busListSpec.noDataEmptyMessageInfo)
        : this.noListDataMessage;
  }

  protected setNoChartableDataMessage() {
    this.emptyMessage = this.noChartableDataAvailable;
  }

  public get componentDestroyed(): boolean {
    return this.m_componentDestroyed;
  }

  protected setComponentDestroyed() {
    this.m_componentDestroyed = true;
  }

  protected validateInitialInputs(): void {
    if (!this.listSpec) {
      throw 'component requires listSpec before ngOnInit';
    }
    if (!this.listState) {
      throw 'component requires listState before ngOnInit';
    }
    if (!this.listPersonalization) {
      throw 'component requires listPersonalization before ngOnInit';
    }
  }

  protected _hidePagerBar(): boolean {
    if (this.listSpec.showAsMingleWidget) {
      return true;
    }

    if (this.dataViewSet && this.dataViewSet.data) {

      // If the list has no data, hide the pager bar
      if (this.dataViewSet.data.length === 0) {
        return true;
      }

      if (this.listSpec.listType === 'multilist'
        && !this.dataViewSet.pagingInfo.hasNext
        && !this.dataViewSet.pagingInfo.hasPrevious
        &&  this.dataViewSet.data.length <= this.listSpec.pageSize) {
        // for multi-list listSpec.pageSize is the fixedNumberOfRows defined in the LPL
        // todo: maybe make a fixedNumberOfRows param on the LMListSpec definition
        return true;
      }
    }

    return false;
  }

  protected isBusyPaging(): boolean {
    return this.pagingSubscription && !this.pagingSubscription.closed;
  }

  protected cancelOutstandingPagingRequest(): void {
    if (this.pagingSubscription && !this.pagingSubscription.closed) {
      // ----------------------------------------------------------------
      // If a call to the back end is still out then we want to
      // unsubscribe from it to cancel the client side processing
      // of the http client request.
      // ----------------------------------------------------------------
      this.lmLoggerService.log([ this, this.instanceId, 'cancelOutstandingPagingRequest(): pagingSubscription is still open, unsubscribing...' ]);
      this.pagingSubscription.unsubscribe();
    }
  }
}
